<template>
    <div :class="[
        'z-input',
        `z-input--size-${size}`,
        {
            'z-input--filter': isFilter,
            'z-input--clearable': isClearable,
            [`z-input--icon-${iconPosition}`]: icon
        },
        {
            'is-focused': isFocused,
            'is-filled': value || value === 0,
            'is-disabled': disabled,
            'is-required': required,
            'is-error': isValid === false,
            'is-valid': isValid && !isFilter,
        }
    ]">
        <label class="z-input__label">
            <p
                class="z-input__title"
                v-if="title"
                v-html="title"
            ></p>
            <div class="z-input__container">
                <z-icon
                    v-if="icon && iconPosition === 'left'"
                    class="z-input__icon z-input__icon--left"
                    :name="icon"
                ></z-icon>
                <div :class="['z-input__input-wrapper', {
                    'z-input__input-wrapper--ellipsis': !isFocused
                }]">
                    <input
                        class="z-input__input"
                        :name="name"
                        :type="type"
                        v-model="value"
                        @blur="onBlur"
                        @focus="onFocus"
                        @change="onChange($event)"
                        @input="onInput($event)"
                        :disabled="disabled"
                        :aria-label="placeholder"
                        @keypress="onKeyPress"
                        ref="inputEl"
                    />
                    <span
                        v-if="isClearable && value"
                        class="z-input__clear"
                        @click.stop="clear"
                    ></span>

                    <span
                        v-show="placeholder"
                        v-html="placeholder"
                        class="z-input__placeholder"
                    ></span>
                </div>
                <z-icon
                    v-if="icon && iconPosition === 'right'"
                    class="z-input__icon z-input__icon--right"
                    :name="icon"
                ></z-icon>
            </div>
        </label>
        <span
            class="z-input__error"
            v-html="error"
            v-if="error && isValid === false"
        ></span>
    </div>
</template>


<script setup>
import { computed, nextTick, ref, onMounted } from 'vue'
import IMask from 'imask'
import {
    validateEmail,
    validateLength,
    validateTel,
    validateTextOnly,
    validateUrl
} from './utils.js'


import {localize} from '@/utils/i18n'

const props = defineProps({
    modelValue: String,
    isFilter: Boolean,
    clearable: Boolean,
    disabled: Boolean,
    required: Boolean,
    title: String,
    placeholder: String,
    name: String,
    icon: {
        type: String
    },
    iconPosition: {
        type: String,
        default: 'right',
        validator: prop => ['right', 'left'].includes(prop)
    },
    size: {
        type: String,
        default: 'l',
        validator: prop => ['l', 'm', 's'].includes(prop)
    },
    type: {
        type: String,
        validator: prop => ['text', 'number', 'tel', 'email', 'link', 'text-only'].includes(prop),
        default: 'text'
    },
    minVal: {
        type: [String, Number]
    },
    maxVal: {
        type: [String, Number]
    },
    positive: {
        type: Boolean,
        default: true
    },
    minLength: {
        type: Number
    },
    mask: {
        type: Object,
        default: {
            mask: ''
        }
    }
})

const locales = {
    errors: {
        required: localize({
            ru: 'Поле обязательно для заполнения',
            en: 'Required field',
            cn: '必填项'
        }),
        invalid: {
            email: localize({
                ru: 'Неверный формат email',
                en: 'Invalid email format',
                cn: '无效的电子邮件格式'
            }),
            url: localize({
                ru: 'Неверный формат ссылки',
                en: 'Invalid link format',
                cn: 'Invalid link format'
            }),
            text: localize({
                ru: 'Разрешены только буквы и специальные символы',
                en: 'Only letters and special characters are allowed',
                cn: '只允许使用字母和特殊字符'
            }),
            tel: localize({
                ru: 'Разрешены только цифры и специальные символы',
                en: 'Only numbers and special characters are allowed',
                cn: '只允许使用数字和特殊字符'
            }),
            pattern: localize({
                ru: 'Неверный формат',
                en: 'Incorrect data format',
                cn: 'Incorrect data format'
            }),
            minVal: localize({
                ru: 'Минимальное значение',
                en: 'Minimum allowable value',
                cn: 'Minimum allowable value'
            }),
            maxVal: localize({
                ru: 'Максимальное значение',
                en: 'Maximum allowable value',
                cn: 'Maximum allowable value'
            }),
            minLength: localize({
                ru: 'Минимальное количество символов',
                en: 'Minimum number of characters',
                cn: 'Minimum number of characters'
            })
        }
    }
}

const emit = defineEmits([
    'update:modelValue',
    'change',
    'input'
])

// ref
let localValue = ref('')
let isFocused = ref(false)
let isValid = ref(null)
let error = ref('')
const inputEl = ref(null)

onMounted(() => {
    if(props.mask.mask) {
        const mask = IMask(inputEl.value, props.mask);
    }
})

// computed
const value = computed({
    get() {
        if (props.modelValue !== undefined) return props.modelValue
        return localValue.value
    },
    set(value) {
        localValue.value = value
        emit('update:modelValue', value)
    }
})
const isClearable = computed(() => props.isFilter || props.clearable)

// events
const onBlur = () => isFocused.value = false
const onFocus = () => isFocused.value = true
const onChange = e => emit('change', e.target.value)
const onInput = e => {
    emit('input', e.target.value)
    validate()
}
const onKeyPress = e => {
    if (props.type !== 'number') return
    if (props.positive && (e.charCode > 31 && (e.charCode < 48 || e.charCode > 57)) && e.charCode !== 46) {
        e.preventDefault()
    }
}

// methods
const clear = async () => {
    emit('update:modelValue', '')
    localValue.value = ''
    emit('input', '')
    emit('change', '')
    await nextTick()
    validate()
}

const validate = () => {
    if (props.required && !value.value) {
        isValid.value = false
        error.value = locales.errors.required
        return
    }

    if (props.minLength && !validateLength(value.value, props.minLength)) {
        isValid.value = false
        error.value = `${locales.errors.invalid.minLength} - ${props.minLength}`
        return
    }

    if (!props.required && !value.value) {
        isValid.value = null
        error.value = ''
        return
    }

    if (props.type === 'email' && !validateEmail(value.value)) {
        isValid.value = false
        error.value = locales.errors.invalid.email
        return
    }

    if (props.type === 'link' && !validateUrl(value.value)) {
        isValid.value = false
        error.value = locales.errors.invalid.url
        return
    }

    if (props.type === 'text-only' && !validateTextOnly(value.value)) {
        isValid.value = false
        error.value = locales.errors.invalid.text
        return
    }

    if (props.type === 'tel' && !validateTel(value.value)) {
        isValid.value = false
        error.value = locales.errors.invalid.tel
        return
    }

    if (props.type === 'number') {
        if (props.minVal && Number(value.value) < Number(props.minVal)) {
            isValid.value = false
            error.value = `${locales.errors.invalid.minVal} - ${props.minVal}`
            return
        }

        if (props.maxVal && Number(value.value) > Number(props.maxVal)) {
            isValid.value = false
            error.value = `${locales.errors.invalid.maxVal} - ${props.maxVal}`
            return
        }
    }

    value.value ? isValid.value = true : isValid.value = null
    error.value = ''
}

defineExpose({
    validate,
    error,
    isValid
})
</script>

<style lang="scss" src="./index.scss"></style>
